import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import proDocumentsService from '../../../services/proDocuments.service';
import type { ProDocumentRow, ProDocumentUpdate } from '../../../types/proDocuments';
import DocsTable from '../components/docs/DocsTable';
import DocsUploadForm from '../components/docs/DocsUploadForm';
import DocsEditDialog from '../components/docs/DocsEditDialog';
import WarningConfirmModal from '../../../components/ui/WarningConfirmModal';
import { StagedDoc, nextTempId } from '../createOrder/stagedDocs';

export interface StagedDocsTabProps {
  stagedDocs: StagedDoc[];
  onChange: (next: StagedDoc[]) => void;
  /** 'T###' — when set, shows the template's docs read-only underneath. */
  templatePro?: string;
}

// No real pronumber exists pre-create; DocsUploadForm only uses this for a
// cosmetic data-pronumber attribute.
const CREATE_PRONUMBER_SENTINEL = 'NEW';

function extFromFilename(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot >= 0 ? name.slice(dot + 1).toLowerCase() : '';
}

/** Presents a StagedDoc as the row shape DocsTable/DocsEditDialog expect.
 *  map_recno/fileinfo_recno = tempId; every server-only field is a stand-in. */
function stagedDocToRow(doc: StagedDoc): ProDocumentRow {
  return {
    map_recno: doc.tempId,
    fileinfo_recno: doc.tempId,
    pronumber: 0,
    documenttype: doc.documenttype,
    description: doc.description,
    driver: doc.driver,
    driverssn: null,
    forinvoice: doc.forinvoice,
    filename: doc.file.name,
    filetype: extFromFilename(doc.file.name),
    directory: '',
    uploaddatetime: new Date().toISOString(),
    uploadedby: '',
    deleted: 'N',
    source: 'cms',
    blob_url: '',
    preview_url: '',
    download_url: '',
  };
}

/**
 * Create-mode Docs tab. A SEPARATE component from DocsTab: DocsTab's
 * react-query mutations fire immediately against a persisted pro, which
 * doesn't exist yet here — every edit below only rewrites the `stagedDocs`
 * array via `onChange`. No document is uploaded until `replayStagedDocs`
 * runs post-create (stagedDocs.ts).
 */
export default function StagedDocsTab({
  stagedDocs,
  onChange,
  templatePro,
}: StagedDocsTabProps) {
  // Batched drops call onUpload once per file in a tight loop; onChange may
  // not have re-rendered this component between calls, so a ref mirrors the
  // latest committed array to avoid two files in one drop clobbering each
  // other's staged row.
  const stagedDocsRef = useRef(stagedDocs);
  stagedDocsRef.current = stagedDocs;

  const [editingRows, setEditingRows] = useState<ProDocumentRow[]>([]);
  const [editingFocus, setEditingFocus] = useState<number | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);

  const docTypesQuery = useQuery({
    queryKey: ['docTypes'],
    queryFn: () => proDocumentsService.documentTypes(),
    staleTime: 5 * 60 * 1000,
  });

  // Read-only: what the create will copy over from the template. No
  // mutations ever run against this query.
  const templateDocsQuery = useQuery({
    queryKey: ['proDocs', templatePro],
    queryFn: () => proDocumentsService.list(templatePro as string),
    enabled: !!templatePro,
  });

  const rows = useMemo(() => stagedDocs.map(stagedDocToRow), [stagedDocs]);

  const commit = useCallback(
    (next: StagedDoc[]) => {
      stagedDocsRef.current = next;
      onChange(next);
    },
    [onChange],
  );

  // DocsUploadForm always calls onUpload(file, {}) — metadata is collected in
  // the edit dialog afterward (same contract DocsTab uses).
  const handleStageUpload = useCallback(
    async (file: File, _meta: ProDocumentUpdate): Promise<ProDocumentRow | null> => {
      const doc: StagedDoc = {
        tempId: nextTempId(),
        file,
        documenttype: '',
        description: '',
        driver: '',
        forinvoice: 'N',
      };
      commit([...stagedDocsRef.current, doc]);
      return stagedDocToRow(doc);
    },
    [commit],
  );

  // Fires once per drop/pick after every file in the batch has staged —
  // opens the edit dialog on the whole batch (DocsTab.tsx L217-221).
  const handleUploaded = (uploadedRows: ProDocumentRow[]) => {
    if (uploadedRows.length === 0) return;
    setEditingRows(uploadedRows);
    setEditingFocus(uploadedRows[0]?.map_recno ?? null);
  };

  const openEditFor = (row: ProDocumentRow) => {
    setEditingRows([row]);
    setEditingFocus(row.map_recno);
  };

  const closeEditDialog = () => {
    setEditingRows([]);
    setEditingFocus(null);
  };

  const handleEditSave = async (mapRecno: number, update: ProDocumentUpdate) => {
    commit(
      stagedDocsRef.current.map((d) =>
        d.tempId === mapRecno
          ? {
              ...d,
              documenttype: update.documenttype ?? d.documenttype,
              description: update.description ?? d.description,
              driver: update.driver ?? d.driver,
              forinvoice: update.forinvoice ?? d.forinvoice,
            }
          : d,
      ),
    );
  };

  // forinvoice has no server toggle pre-create — it only ever edits staged
  // metadata, replayed for real when the doc uploads after create.
  const handleToggleInvoice = (row: ProDocumentRow, value: 'Y' | 'N') => {
    commit(
      stagedDocsRef.current.map((d) =>
        d.tempId === row.map_recno ? { ...d, forinvoice: value } : d,
      ),
    );
  };

  const handleDelete = (row: ProDocumentRow) => setConfirmDeleteId(row.map_recno);

  const confirmDeleteDoc = useMemo(
    () => stagedDocs.find((d) => d.tempId === confirmDeleteId) ?? null,
    [stagedDocs, confirmDeleteId],
  );

  const handleConfirmDelete = () => {
    if (confirmDeleteId == null) return;
    commit(stagedDocsRef.current.filter((d) => d.tempId !== confirmDeleteId));
    setConfirmDeleteId(null);
  };

  // Staged docs have no server-side history yet — nothing to show.
  const handleHistory = useCallback(() => {}, []);

  // No durable blob to preview from the server; open a local object URL when
  // the runtime supports it rather than wiring a real preview endpoint.
  const handlePreview = useCallback(
    (row: ProDocumentRow) => {
      const doc = stagedDocs.find((d) => d.tempId === row.map_recno);
      if (!doc || typeof URL.createObjectURL !== 'function') return;
      const url = URL.createObjectURL(doc.file);
      window.open(url, '_blank', 'noopener');
    },
    [stagedDocs],
  );

  return (
    <div className="staged-docs-tab flex h-full min-h-0 flex-col gap-3 p-3">
      <div className="shrink-0">
        <DocsUploadForm
          pronumber={CREATE_PRONUMBER_SENTINEL}
          onUpload={handleStageUpload}
          onUploaded={handleUploaded}
        />
      </div>
      <div className="min-h-0 flex-1">
        <DocsTable
          rows={rows}
          loading={false}
          onEdit={openEditFor}
          onHistory={handleHistory}
          onDelete={handleDelete}
          onPreview={handlePreview}
          onToggleInvoice={handleToggleInvoice}
          enablePrint={false}
          emptyMessage="No documents staged yet."
        />
      </div>
      {templatePro && (
        <div className="shrink-0 rounded-md border border-theme-border-primary p-3">
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">
            From template — copied to the new order at create
          </div>
          {templateDocsQuery.isLoading ? (
            <div className="text-sm text-muted">Loading…</div>
          ) : (templateDocsQuery.data || []).length === 0 ? (
            <div className="text-sm text-muted">
              No documents on the template.
            </div>
          ) : (
            <ul className="space-y-1 text-sm">
              {(templateDocsQuery.data || []).map((doc) => (
                <li
                  key={doc.map_recno}
                  className="flex items-center gap-2 text-gray-700 dark:text-gray-200"
                >
                  <span className="font-medium">{doc.filename}</span>
                  {doc.documenttype && (
                    <span className="text-muted">
                      — {doc.documenttype}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      <DocsEditDialog
        rows={editingRows}
        initialMapRecno={editingFocus}
        docTypes={docTypesQuery.data || []}
        drivers={[]}
        onSave={handleEditSave}
        onClose={closeEditDialog}
      />
      <WarningConfirmModal
        open={confirmDeleteId != null}
        title="Remove Document"
        message={
          confirmDeleteDoc ? (
            <>
              Remove <span className="font-semibold">{confirmDeleteDoc.file.name}</span> from
              this order? It has not been uploaded yet.
            </>
          ) : (
            ''
          )
        }
        confirmLabel="Remove"
        cancelLabel="Cancel"
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmDeleteId(null)}
      />
    </div>
  );
}
